import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, IconButton, Chip,
    Modal, ModalDialog, ModalClose, Textarea,
} from '@mui/joy';
import { motion } from 'framer-motion';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';

import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import { AppContext } from '../../context';
import {
    APIGetPendingOrderDeletionRequests, APIReviewOrderDeletionRequest, parseApiError,
} from '../../api';

const fmtMoney = (v) => Number(v || 0).toLocaleString('ru-RU', { maximumFractionDigits: 0 });
const fmtDate  = (iso) => (iso ? new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }) : '—');

export default function OrderDeletionRequestsPage() {
    const { token, showToast, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const [reqs, setReqs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [rejectModal, setRejectModal] = useState({ open: false, req: null, comment: '' });

    const load = useCallback(async () => {
        setLoading(true);
        const res = await APIGetPendingOrderDeletionRequests(token);
        setReqs(Array.isArray(res) ? res : []);
        setLoading(false);
    }, [token]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => onRefreshTick(load), [onRefreshTick, load]);

    const apply = async (req, action, comment = '') => {
        setBusyId(req.id);
        const res = await APIReviewOrderDeletionRequest(token, req.id, action, comment);
        setBusyId(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось'), 'danger'); return; }

        const isCancel = (req.kind ?? 'cancel') === 'cancel';
        showToast(
            action === 'approve'
                ? (isCancel ? 'Чек отменён ✓' : 'Чек удалён ✓')
                : 'Заявка отклонена',
            action === 'approve' ? 'success' : 'warning',
        );
        setReqs(prev => prev.filter(r => r.id !== req.id));
    };

    const submitReject = async () => {
        const { req, comment } = rejectModal;
        setRejectModal({ open: false, req: null, comment: '' });
        await apply(req, 'reject', comment.trim());
    };

    return (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <IconButton variant="plain" onClick={() => navigate('/manager')} sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        <ArrowBackRoundedIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontSize: 22, fontWeight: 800 }}>Заявки на отмену чеков</Typography>
                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                            Админы не отменяют чеки сами — только по согласованию
                        </Typography>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><MemeLoader /></Box>
                ) : reqs.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 8, borderRadius: 'var(--joy-radius-xl)',
                        border: '1px dashed var(--joy-palette-divider)', bgcolor: 'var(--joy-palette-background-level1)',
                    }}>
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет заявок на рассмотрении
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {reqs.map(req => {
                            const isCancel = (req.kind ?? 'cancel') === 'cancel';
                            return (
                                <Box key={req.id} sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderLeft: `3px solid ${isCancel ? '#f59e0b' : 'var(--joy-palette-danger-400)'}`,
                                    borderRadius: 'var(--joy-radius-lg)', p: 2,
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.8 }}>
                                        <Box sx={{ minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                                                <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
                                                    Чек на {fmtMoney(req.amount_snapshot)} ₽
                                                </Typography>
                                                <Chip size="sm" variant="soft" color={isCancel ? 'warning' : 'danger'}
                                                    sx={{ fontSize: 9, fontWeight: 700, '--Chip-minHeight': '18px' }}>
                                                    {req.kind_display || (isCancel ? 'Отмена чека' : 'Удаление чека')}
                                                </Chip>
                                            </Box>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                                {req.photographer_name_snapshot || '—'}
                                                {req.location_name ? ` · ${req.location_name}` : ''}
                                                {req.date ? ` · ${fmtDate(req.date)}` : ''}
                                            </Typography>
                                        </Box>
                                        <Chip size="sm" variant="soft" color="neutral" sx={{ fontSize: 10, fontWeight: 700, height: 22 }}>
                                            {req.acting_admin_name || req.requested_by_name || 'аккаунт'}
                                        </Chip>
                                    </Box>

                                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-primary)', mb: 1.5 }}>
                                        Причина: {req.comment}
                                    </Typography>

                                    {req.order_is_cancelled && (
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: 'var(--joy-palette-danger-400)', mb: 1 }}>
                                            Чек уже отменён — заявку можно просто закрыть
                                        </Typography>
                                    )}

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button size="sm" variant="soft" color="neutral" disabled={busyId === req.id}
                                            onClick={() => setRejectModal({ open: true, req, comment: '' })}
                                            sx={{ fontWeight: 700 }}>
                                            Отклонить
                                        </Button>
                                        <Button size="sm" color={isCancel ? 'warning' : 'danger'} loading={busyId === req.id}
                                            onClick={() => apply(req, 'approve')} sx={{ fontWeight: 800 }}>
                                            {isCancel ? 'Одобрить и отменить' : 'Одобрить и удалить'}
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            {/* Модалка отклонения — вместо window.prompt */}
            <Modal open={rejectModal.open} onClose={() => setRejectModal({ open: false, req: null, comment: '' })}>
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-divider)',
                    borderRadius: 'var(--joy-radius-xl)', maxWidth: 420, width: '100%', p: { xs: 2, sm: 3 },
                    maxHeight: 'calc(100dvh - 32px)', overflowY: 'auto',
                }}>
                    <ModalClose />
                    <Typography sx={{ fontSize: 17, fontWeight: 800, mb: 0.5 }}>Отклонить заявку</Typography>
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                        Комментарий необязателен, но он придёт автору заявки в уведомлении.
                    </Typography>
                    <Textarea minRows={2} maxRows={4}
                        placeholder="Например: чек оформлен верно, отменять не нужно"
                        value={rejectModal.comment}
                        onChange={(e) => setRejectModal(p => ({ ...p, comment: e.target.value.slice(0, 300) }))}
                        sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button color="neutral" onClick={submitReject} sx={{ flex: 1, fontWeight: 800 }}>
                            Отклонить заявку
                        </Button>
                        <Button variant="outlined" onClick={() => setRejectModal({ open: false, req: null, comment: '' })}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)' }}>
                            Назад
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        </motion.div>
    );
}